import { useAuth } from "../contexts/AuthContext";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { propertiesService } from "../services/propertiesService";
import { Plus, Search, Building, MapPin, Euro, Calendar, User, X, Home, DollarSign, FileText, CheckCircle, AlertCircle, Users, TrendingUp, Filter, Download, Bell, ChevronDown, ChevronLeft, ChevronRight, Sparkles, Calculator, Save } from "lucide-react";
import UnitsManagerModal from "../components/units/UnitsManagerModal";
import PropertyModal from "../components/PropertyModal";
import { masterSupabase } from '@/lib/supabase-master.js';


// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-[60] max-w-sm w-full bg-white dark:bg-slate-900 shadow-lg rounded-lg border-l-4 ${
      type === "success" ? "border-green-500" : "border-red-500"
    } p-4`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          {type === "success" ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <AlertCircle className="h-5 w-5 text-red-500" />
          )}
        </div>
        <div className="ml-3">
          <p className={`text-sm font-medium ${
            type === "success" ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"
          }`}>
            {message}
          </p>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              onClick={onClose}
              className={`inline-flex rounded-md p-1.5 ${
                type === "success" 
                  ? "text-green-500 hover:bg-green-100 dark:hover:bg-green-800" 
                  : "text-red-500 hover:bg-red-100 dark:hover:bg-red-800"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                type === "success" ? "focus:ring-green-500" : "focus:ring-red-500"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 🎨 Cool Progress Bar Component
const ProgressBar = ({ percentage, color = 'blue', label, value }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600 shadow-blue-200',
    green: 'from-green-500 to-green-600 shadow-green-200',
    yellow: 'from-yellow-500 to-yellow-600 shadow-yellow-200',
    orange: 'from-orange-500 to-orange-600 shadow-orange-200',
    red: 'from-red-500 to-red-600 shadow-red-200'
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-sm text-gray-600 dark:text-gray-400">{value}</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-slate-800 rounded-full h-2.5 overflow-hidden">
        <div 
          className={`h-full bg-gradient-to-r ${colorClasses[color]} transition-all duration-1000 ease-out shadow-sm`}
          style={{ 
            width: `${Math.min(Math.max(percentage, 0), 100)}%`,
            boxShadow: `0 0 8px ${colorClasses[color].includes('blue') ? '#3b82f6' : 
                                   colorClasses[color].includes('green') ? '#10b981' :
                                   colorClasses[color].includes('yellow') ? '#f59e0b' :
                                   colorClasses[color].includes('orange') ? '#f97316' : '#ef4444'}33`
          }}
        />
      </div>
    </div>
  );
};

const PropertiesPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [propertyMetrics, setPropertyMetrics] = useState({}); // NEW: Store metrics for each property
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOwner, setSelectedOwner] = useState("all");
  const [selectedOwnerName, setSelectedOwnerName] = useState("Alle Eigentümer");
  const [selectedProperty, setSelectedProperty] = useState("all");

  // Format currency helper function
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const [selectedPropertyName, setSelectedPropertyName] = useState("Alle Objekte");
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);
  const [toast, setToast] = useState(null);
  
  // 🤖 Clara Insights States (Analytics-Style)
  const [showClaraInsights, setShowClaraInsights] = useState(false);
  const [claraInsightsData, setClaraInsightsData] = useState(null);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [selectedInsightProperty, setSelectedInsightProperty] = useState(null);
  const [currentInsightCategory, setCurrentInsightCategory] = useState(0); // NEW: For navigation
  const [insightCategories, setInsightCategories] = useState([]); // NEW: Multiple categories

  // 🏠 Units Modal States
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [selectedPropertyForUnits, setSelectedPropertyForUnits] = useState(null);
  
  // ✏️ Edit Property Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPropertyForEdit, setSelectedPropertyForEdit] = useState(null);
  
  // ➕ Create Property Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Refs
  const isMountedRef = useRef(true);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Handle URL parameters for owner selection
  useEffect(() => {
    const ownerIdFromUrl = searchParams.get('owner_id');
    console.log('🔍 DEBUG: URL Parameter owner_id:', ownerIdFromUrl, 'Type:', typeof ownerIdFromUrl);
    console.log('🔍 DEBUG: Current selectedOwner:', selectedOwner, 'Type:', typeof selectedOwner);
    console.log('🔍 DEBUG: Available owners:', owners.map(o => ({id: o.id, name: o.name, idType: typeof o.id})));
    
    // Only process if we have owners loaded and URL parameter exists
    if (ownerIdFromUrl && owners.length > 0 && ownerIdFromUrl !== selectedOwner) {
      console.log('🔄 DEBUG: Setting selectedOwner to:', ownerIdFromUrl);
      setSelectedOwner(ownerIdFromUrl);
      
      // Find owner name for display - support both string and integer IDs
      const owner = owners.find(o => 
        o.id == ownerIdFromUrl || 
        o.id === parseInt(ownerIdFromUrl) ||
        String(o.id) === ownerIdFromUrl
      );
      console.log('🔍 DEBUG: Found owner:', owner);
      
      if (owner) {
        console.log('✅ DEBUG: Setting owner name to:', owner.name);
        setSelectedOwnerName(owner.name);
      } else {
        console.log('❌ DEBUG: No owner found for ID:', ownerIdFromUrl);
        // Fallback: Set a generic name if owner not found but ID exists
        setSelectedOwnerName(`Eigentümer (${ownerIdFromUrl})`);
      }
    }
  }, [searchParams, owners]); // Depend on both searchParams AND owners

  // Auto-select single property when owner is filtered
  useEffect(() => {
    if (selectedOwner !== "all" && properties.length === 1) {
      // If only one property is shown, auto-select it
      const singleProperty = properties[0];
      setSelectedProperty(singleProperty.id);
      setSelectedPropertyName(singleProperty.property_name || `${singleProperty.street}, ${singleProperty.city}`);
      console.log('🎯 DEBUG: Auto-selected single property:', singleProperty.property_name);
    } else if (selectedOwner === "all") {
      // Reset to "Alle Objekte" when showing all owners
      setSelectedProperty("all");
      setSelectedPropertyName("Alle Objekte");
    }
  }, [properties, selectedOwner]);

  // Load properties when owner changes
  useEffect(() => {
    console.log('🔄 DEBUG: selectedOwner changed to:', selectedOwner, 'Type:', typeof selectedOwner);
    if (selectedOwner !== "all") {
      console.log('📋 DEBUG: Loading filtered properties for owner:', selectedOwner);
      loadFilteredProperties();
    } else {
      console.log('📋 DEBUG: Loading all properties');
      loadProperties();
    }
  }, [selectedOwner]);

  // Load metrics when properties change
  useEffect(() => {
    if (properties.length > 0) {
      loadAllPropertyMetrics();
    }
  }, [properties]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadOwners(),
        loadProperties()
      ]);
    } catch (error) {
      console.error("Error loading data:", error);
      showToast("Fehler beim Laden der Daten", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadOwners = async () => {
    try {
      console.log('🔄 PropertiesPage: Loading owners from Supabase...');
      
      const { data: ownersData, error } = await masterSupabase
        .from('owners')
        .select('*')
        .eq('created_by', user.id)
        .order('name');

      if (error) throw error;
      
      console.log('✅ PropertiesPage: Owners loaded:', ownersData?.length || 0);
      setOwners(ownersData || []);
    } catch (error) {
      console.error('❌ PropertiesPage: Error loading owners:', error);
      setOwners([]);
    }
  };


  // Handle Property Save Function
  const handlePropertySave = async () => {
    console.log("💾 Property save called");
    
    try {
        const modal = document.querySelector('[class*="fixed inset-0"]');
      if (!modal) {
        setToast({ message: "Modal nicht gefunden", type: "error" });
        return;
      }
      
      const inputs = modal.querySelectorAll("input, select, textarea");
      const propertyData = {};
      
      console.log("🔍 Total inputs found:", inputs.length);
      
      inputs.forEach((input, index) => {
        const labelElement = input.closest("div")?.querySelector("label");
        const labelText = labelElement?.textContent?.trim();
        
        console.log(`🔍 Input ${index}:`, {
          value: input.value,
          type: input.type,
          hasValue: !!input.value,
          labelText: labelText,
          inputHTML: input.outerHTML.substring(0, 100)
        });
        
        if (input.value && input.value.trim() !== "") {
          const fieldMapping = {
            // Stammdaten
            "Objektname": "property_name",
            "Straße": "street",
            "Stadt": "city",
            "PLZ": "postal_code",
            "Land": "country",
            "Immobilienstatus": "property_status",
            "Beschreibung": "description",
            
            // Finanzielle Daten
            "Kaufpreis (€)": "purchase_price",
            "Kaufdatum": "purchase_date",
            "Monatliche Mieteinnahmen (€)": "monthly_rent_total",
            "Geschätzter Marktwert (€)": "estimated_market_value",
            "Eigenkapital (€)": "financing_equity",
            "Fremdkapital (€)": "financing_debt",
            "Bodenanteil (%)": "land_share_percent",
            "AfA-Satz Überschreibung (%)": "depreciation_rate_override",
            
            // Technische Daten
            "Baujahr": "year_built",
            "Fertigstellungsjahr": "completion_year",
            "Gesamtfläche (m²)": "total_square_meters",
            "Vermietbare Fläche (m²)": "rentable_area",
            "Grundstücksfläche (m²)": "ground_area",
            "Anzahl Etagen": "number_of_floors",
            "Energieeffizienzklasse": "energy_efficiency_class",
            
            // Verwaltung
            "Eigentümer": "owner_entity",
            "Bank": "bank_name",
            "Bankkonto": "bank_account_id",
            "Allgemeine Notizen": "notes"
          };
          
          const dbField = fieldMapping[labelText];
          if (dbField) {
            // Integer fields
            if (["year_built", "completion_year", "number_of_floors"].includes(dbField)) {
              propertyData[dbField] = parseInt(input.value) || null;
            } 
            // Float/Decimal fields
            else if (["total_area", "total_square_meters", "rentable_area", "ground_area", 
                      "purchase_price", "monthly_rent_total", "estimated_market_value",
                      "financing_equity", "financing_debt", "land_share_percent",
                      "depreciation_rate_override"].includes(dbField)) {
              propertyData[dbField] = parseFloat(input.value) || null;
            } 
            // Date fields
            else if (["purchase_date"].includes(dbField)) {
              propertyData[dbField] = input.value.trim() || null;
            } 
            // String fields
            else {
              propertyData[dbField] = input.value.trim();
            }
          }
        }
      });
      
      if (propertyData.street && propertyData.city && propertyData.postal_code) {
        propertyData.address = `${propertyData.street}, ${propertyData.postal_code} ${propertyData.city}`;
      }
      
      // Sync total_area with total_square_meters for compatibility
      if (propertyData.total_square_meters) {
        propertyData.total_area = propertyData.total_square_meters;
      }
      
      console.log("📊 Property data to save:", propertyData);
      console.log("🔍 property_name value:", propertyData.property_name);
      console.log("🔍 Has property_name:", 'property_name' in propertyData);
      
      if (!propertyData.property_name) {
        console.error("❌ FEHLER: property_name ist leer oder undefined!");
        console.log("🔍 Alle gefundenen Felder:", Object.keys(propertyData));
      }
      
      let result;
      
      if (selectedPropertyForEdit?.id) {
        result = await masterSupabase
          .from("properties")
          .update(propertyData)
          .eq("id", selectedPropertyForEdit.id)
          .select();
      } else {
        result = await masterSupabase
          .from("properties")
          .insert([propertyData])
          .select();
      }
      
      const { data, error } = result;
      
      if (error) {
        setToast({ message: `Fehler beim Speichern: ${error.message}`, type: "error" });
        return;
      }
      
      setToast({ message: selectedPropertyForEdit?.id ? "Objekt erfolgreich aktualisiert!" : "Objekt erfolgreich erstellt!", type: "success" });
      
      setShowEditModal(false);
      setShowCreateModal(false);
      setSelectedPropertyForEdit(null);
      
      loadProperties();
      
    } catch (err) {
      setToast({ message: `Unerwarteter Fehler: ${err.message}`, type: "error" });
    }
  };
  const loadProperties = async () => {
    try {
      console.log('🔄 PropertiesPage: Loading properties from Supabase...');
      
      let propertiesQuery = masterSupabase.from("properties").select("*, owners:owner_id (id, name)");
      
      // CRITICAL: Filter by current user first
      if (!user?.id) {
        console.log("🚫 No user ID available, cannot load properties");
        setProperties([]);
        return;
      }
      propertiesQuery = propertiesQuery.eq("created_by", user.id);
      
      if (selectedOwner !== 'all') {
        propertiesQuery = propertiesQuery.eq('owner_id', selectedOwner);
        console.log('🔍 PropertiesPage: Filtering by owner_id:', selectedOwner);
      }
      
      const { data: propertiesData, error } = await propertiesQuery.order('property_name');

      if (error) throw error;
      
      console.log('✅ PropertiesPage: Properties loaded:', propertiesData?.length || 0);
      // Transform data to include owner_name for display
      const transformedProperties = propertiesData?.map(property => ({
        ...property,
        owner_name: property.owners?.name || "Unbekannter Eigentümer"
      })) || [];
      setProperties(transformedProperties);
      
      // Reset property selection if current property doesn't exist in filtered list
      if (selectedProperty !== 'all') {
        const propertyExists = propertiesData?.some(p => p.id === selectedProperty);
        if (!propertyExists) {
          setSelectedProperty('all');
          setSelectedPropertyName('Alle Objekte');
        }
      }
    } catch (error) {
      console.error('❌ PropertiesPage: Error loading properties:', error);
      setProperties([]);
    }
  };

  // 📊 Load metrics for all properties
  const loadAllPropertyMetrics = async () => {
    try {
      const metricsPromises = properties.map(async (property) => {
        const metrics = await calculatePropertyMetrics(property);
        return { propertyId: property.id, metrics };
      });

      const metricsResults = await Promise.all(metricsPromises);
      const metricsMap = {};
      metricsResults.forEach(({ propertyId, metrics }) => {
        metricsMap[propertyId] = metrics;
      });

      setPropertyMetrics(metricsMap);
    } catch (error) {
      console.error("Error loading property metrics:", error);
    }
  };

  // 🔧 Load filtered properties based on selected owner
  const loadFilteredProperties = async () => {
    try {
      console.log('🔄 PropertiesPage: Loading filtered properties from Supabase...');
      console.log('🔍 DEBUG: Filtering by owner_id:', selectedOwner, 'Type:', typeof selectedOwner);
      
      const { data: propertiesData, error } = await masterSupabase
        .from('properties')
        .select('*')
        .eq('owner_id', selectedOwner)
        .order('property_name');

      console.log('📊 DEBUG: Supabase query result:', { data: propertiesData, error });
      console.log('📊 DEBUG: Properties found:', propertiesData?.length || 0);

      if (error) throw error;
      
      console.log('✅ PropertiesPage: Filtered properties loaded:', propertiesData?.length || 0);
      setProperties(propertiesData || []);
      
      // Reset property selection if current property doesn't exist in filtered list
      if (selectedProperty !== 'all') {
        const propertyExists = propertiesData?.some(p => p.id === selectedProperty);
        if (!propertyExists) {
          setSelectedProperty('all');
          setSelectedPropertyName('Alle Objekte');
        }
      }
    } catch (error) {
      console.error('❌ PropertiesPage: Error loading filtered properties:', error);
      setProperties([]);
    }
  };

  // 🧮 Calculate Property Metrics from Tenants/Units (DASHBOARD LOGIC)
  const calculatePropertyMetrics = async (property) => {
    try {
      // 1. Load Units for this Property
      const { data: units, error: unitsError } = await masterSupabase
        .from('units')
        .select('*')
        .eq('property_id', property.id);

      if (unitsError) {
        console.error('Error loading units:', unitsError);
        return getDefaultMetrics();
      }

      // 2. Load Tenants for these Units
      const unitIds = (units || []).map(u => u.id);
      let tenants = [];
      if (unitIds.length > 0) {
        const { data: tenantsData, error: tenantsError } = await masterSupabase
          .from('tenants')
          .select('*')
          .in('unit_id', unitIds);

        if (tenantsError) {
          console.error('Error loading tenants:', tenantsError);
        } else {
          tenants = tenantsData || [];
        }
      }

      // 3. Load Documents for this Property (NEW!)
      const tenantIds = tenants.map(t => t.id);
      let documentCount = 0;
      
      try {
        // Count documents linked to property, units, or tenants
        const { count, error: documentsError } = await masterSupabase
          .from('documents')
          .select('id', { count: 'exact' })
          .or(`property_id.eq.${property.id},unit_id.in.(${unitIds.join(',')}),tenant_id.in.(${tenantIds.join(',')})`);

        if (documentsError) {
          console.error('Error loading documents count:', documentsError);
        } else {
          documentCount = count || 0;
        }
      } catch (docError) {
        console.error('Error counting documents:', docError);
        documentCount = 0;
      }

      // 4. Calculate metrics using correct logic
      const monthlyRent = tenants.reduce((sum, tenant) => {
        return sum + (parseFloat(tenant.cold_rent) || 0);
      }, 0);

      // CORRECT: Occupancy based on Unit Status, not Tenant count
      const occupiedUnits = units.filter(unit => unit.status === 'occupied').length;
      const totalUnits = units.length;
      const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;

      const purchasePrice = property.purchase_price || 1;
      const yearlyRent = monthlyRent * 12;
      const roi = purchasePrice > 0 ? ((yearlyRent / purchasePrice) * 100) : 0;
      const cashflow = Math.round(monthlyRent * 0.75); // 75% after costs

      return {
        occupancyRate: Math.round(occupancyRate),
        roi: parseFloat(roi.toFixed(1)),
        cashflow,
        monthlyRent,
        yearlyRent,
        purchasePrice,
        totalUnits,
        occupiedUnits,
        documentCount
      };
    } catch (error) {
      console.error('Error calculating property metrics:', error);
      return getDefaultMetrics();
    }
  };

  // Default metrics when calculation fails
  const getDefaultMetrics = () => ({
    occupancyRate: 0,
    roi: 0,
    cashflow: 0,
    monthlyRent: 0,
    yearlyRent: 0,
    purchasePrice: 0,
    totalUnits: 0,
    occupiedUnits: 0,
    documentCount: 0
  });

  // 🚦 Calculate Property Performance Score and Border Color
  const getPropertyPerformance = (metrics) => {
    // Performance Score: Auslastung (50%) + ROI (30%) + Cashflow (20%)
    let score = 0;
    
    // Auslastung (50% Gewichtung)
    score += (metrics.occupancyRate / 100) * 50;
    
    // ROI Performance (30% Gewichtung)  
    score += Math.min(metrics.roi / 8, 1) * 30;
    
    // Cashflow positiv/negativ (20% Gewichtung)
    score += (metrics.cashflow > 0 ? 1 : 0) * 20;
    
    const finalScore = Math.round(score);
    
    // Farbzuordnung basierend auf Score
    if (finalScore >= 80) {
      return { score: finalScore, color: 'green', borderClass: 'border-green-500 shadow-green-200' };
    } else if (finalScore >= 60) {
      return { score: finalScore, color: 'yellow', borderClass: 'border-yellow-500 shadow-yellow-200' };
    } else if (finalScore >= 40) {
      return { score: finalScore, color: 'orange', borderClass: 'border-orange-500 shadow-orange-200' };
    } else {
      return { score: finalScore, color: 'red', borderClass: 'border-red-500 shadow-red-200' };
    }
  };

  // 🧭 Navigation functions for insight categories
  const navigateInsightCategory = (direction) => {
    if (!insightCategories.length) return;
    
    let newIndex = currentInsightCategory;
    if (direction === 'prev') {
      newIndex = currentInsightCategory > 0 ? currentInsightCategory - 1 : insightCategories.length - 1;
    } else {
      newIndex = currentInsightCategory < insightCategories.length - 1 ? currentInsightCategory + 1 : 0;
    }
    
    setCurrentInsightCategory(newIndex);
    setClaraInsightsData(insightCategories[newIndex]?.insights || []);
  };

  // 🤖 Generate Clara Insights (Advanced Multi-Category Analysis)
  const generateClaraInsights = useCallback(async (property) => {
    if (generatingInsights) return;
    
    setGeneratingInsights(true);
    setSelectedInsightProperty(property);
    setShowClaraInsights(true);
    setCurrentInsightCategory(0);
    
    // Auto-scroll to top with proper timing
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    
    try {
      // Load comprehensive property data
      const metrics = await calculatePropertyMetrics(property);
      
      // 1. Load Units for detailed analysis
      const { data: units, error: unitsError } = await masterSupabase
        .from('units')
        .select('*')
        .eq('property_id', property.id);

      // 2. Load Tenants with lease information
      const unitIds = (units || []).map(u => u.id);
      let tenants = [];
      if (unitIds.length > 0) {
        const { data: tenantsData, error: tenantsError } = await masterSupabase
          .from('tenants')
          .select('*')
          .in('unit_id', unitIds);
        tenants = tenantsData || [];
      }

      // 3. Load Maintenance data for cost analysis
      let maintenanceData = [];
      try {
        const { data: maintenance, error: maintenanceError } = await masterSupabase
          .from('maintenance')
          .select('*')
          .eq('property_id', property.id)
          .order('created_at', { ascending: false })
          .limit(50);
        maintenanceData = maintenance || [];
      } catch (error) {
        console.log('Maintenance data not available');
      }

      // 🧠 ADVANCED ANALYSIS ALGORITHMS
      
      // === CATEGORY 1: VERTRAGS-INTELLIGENCE ===
      const contractInsights = analyzeContracts(tenants, units);
      
      // === CATEGORY 2: WIRTSCHAFTS-OPTIMIERUNG ===
      const economicInsights = analyzeEconomics(tenants, units, property, metrics);
      
      // === CATEGORY 3: WARTUNGS-PROGNOSE ===
      const maintenanceInsights = analyzeMaintenance(maintenanceData, property, units);
      
      // === CATEGORY 4: MARKT-INTELLIGENCE ===
      const marketInsights = analyzeMarketTrends(property, tenants, metrics);

      const categories = [
        {
          title: "Vertrags-Intelligence",
          icon: "📋",
          insights: contractInsights
        },
        {
          title: "Wirtschafts-Optimierung", 
          icon: "💰",
          insights: economicInsights
        },
        {
          title: "Wartungs-Prognose",
          icon: "🔧", 
          insights: maintenanceInsights
        },
        {
          title: "Markt-Intelligence",
          icon: "📈",
          insights: marketInsights
        }
      ];
      
      setInsightCategories(categories);
      setClaraInsightsData(categories[0]?.insights || []);
      
    } catch (error) {
      console.error("Error generating Clara insights:", error);
      setClaraInsightsData([{
        icon: "⚠️",
        title: "Fehler beim Laden",
        description: "Clara Insights konnten nicht geladen werden",
        category: "System",
        type: 'error',
        confidence: 0,
        action: "Bitte versuchen Sie es erneut"
      }]);
    } finally {
      setGeneratingInsights(false);
    }
  }, [generatingInsights]);

  // 🎨 Insight Card Component
  const InsightCard = ({ insight }) => {
    const colorClasses = {
      green: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300',
      orange: 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300',
      red: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
      blue: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300'
    };

    const IconComponent = insight.icon;

    return (
      <div className={`p-3 rounded-lg border ${colorClasses[insight.color]} transition-all duration-200`}>
        <div className="flex items-start space-x-3">
          <IconComponent className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm">{insight.title}</h4>
            <p className="text-xs mt-1 opacity-90">{insight.description}</p>
          </div>
        </div>
      </div>
    );
  };

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  // Analytics-style dropdown handlers
  const toggleOwnerDropdown = () => {
    setIsOwnerDropdownOpen(!isOwnerDropdownOpen);
    setIsPropertyDropdownOpen(false);
  };

  const togglePropertyDropdown = () => {
    setIsPropertyDropdownOpen(!isPropertyDropdownOpen);
    setIsOwnerDropdownOpen(false);
  };

  const handleOwnerSelect = (ownerId, ownerName) => {
    setSelectedOwner(ownerId);
    setSelectedOwnerName(ownerName);
    setIsOwnerDropdownOpen(false);
    
    // Reset property selection when owner changes
    setSelectedProperty("all");
    setSelectedPropertyName("Alle Objekte");
  };

  const handlePropertySelect = (propertyId, propertyName) => {
    setSelectedProperty(propertyId);
    setSelectedPropertyName(propertyName);
    setIsPropertyDropdownOpen(false);
  };

  const getOwnerDisplayName = (owner) => {
    return owner.name || `Owner ${owner.id}`;
  };

  const getPropertyDisplayName = (property) => {
    return property.property_name || property.name || `Property ${property.id}`;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setIsOwnerDropdownOpen(false);
        setIsPropertyDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Filter properties based on search and selected owner
  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.property_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.street?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.city?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // FIX: Use owner_id instead of owner_entity for consistency with database
    const matchesOwner = selectedOwner === "all" || 
                        property.owner_id === selectedOwner ||
                        String(property.owner_id) === selectedOwner;
    
    console.log('🔍 DEBUG: Filtering property:', property.property_name, 
                'owner_id:', property.owner_id, 
                'selectedOwner:', selectedOwner, 
                'matchesOwner:', matchesOwner);
    
    return matchesSearch && matchesOwner;
  });

  const handleUnitsClick = (property) => {
    setSelectedPropertyForUnits(property);
    setShowUnitsModal(true);
  };

  const handleEditClick = (property) => {
    console.log("🔧 Edit Property clicked:", property);
    setSelectedPropertyForEdit(property);
    setShowEditModal(true);
  };

  const handleCreateClick = () => {
    console.log("➕ Create Property clicked");
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Building className="w-8 h-8 mr-3 text-blue-600" />
            Objekte
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Verwalten Sie Ihr Immobilienportfolio intelligent
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-3">
          <button 
            onClick={() => navigate('/bank-report')}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
          >
            <FileText className="w-5 h-5" />
            Bankreport erstellen
          </button>
          
          <button 
            onClick={() => navigate('/property-valuation')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
          >
            <TrendingUp className="w-5 h-5" />
            Objekt bewerten
          </button>
          
          <button 
            onClick={handleCreateClick}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Neues Objekt
          </button>
        </div>
      </div>

      {/* Clara Insights Panel (Advanced Multi-Category) */}
      {showClaraInsights && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-b border-purple-200 dark:border-purple-800 mb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Header with Navigation */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
                  Clara Insights für {selectedInsightProperty?.property_name || 'Objekt'}
                </h3>
                
                {/* Category Navigation */}
                {insightCategories.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigateInsightCategory('prev')}
                      className="p-2 rounded-full bg-white/50 hover:bg-white/70 dark:bg-slate-900/50 dark:hover:bg-gray-800/70 transition-colors"
                      title="Vorherige Kategorie"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>
                    
                    <div className="flex items-center space-x-2 px-3 py-1 bg-white/60 dark:bg-slate-900/60 rounded-full">
                      <span className="text-lg">{insightCategories[currentInsightCategory]?.icon}</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {insightCategories[currentInsightCategory]?.title}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {currentInsightCategory + 1}/{insightCategories.length}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => navigateInsightCategory('next')}
                      className="p-2 rounded-full bg-white/50 hover:bg-white/70 dark:bg-slate-900/50 dark:hover:bg-gray-800/70 transition-colors"
                      title="Nächste Kategorie"
                    >
                      <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setShowClaraInsights(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold px-3 py-1 rounded-md hover:bg-white/20"
              >
                ×
              </button>
            </div>
            
            {generatingInsights ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
                <span className="text-gray-700 dark:text-gray-300">Clara analysiert das Objekt...</span>
              </div>
            ) : claraInsightsData ? (
              /* 3-Tile Layout (Dashboard Style) */
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {claraInsightsData.slice(0, 3).map((insight, index) => (
                  <div key={index} className="bg-white/90 dark:bg-slate-900/90 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200">
                    {/* Icon and Category Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="text-3xl">{insight.icon}</div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white text-base">
                            {insight.title}
                          </h4>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            insight.category === 'Verträge' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                            insight.category === 'Wirtschaft' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            insight.category === 'Wartung' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                            insight.category === 'Markt' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                            'bg-gray-100 text-gray-800 dark:bg-black dark:text-gray-200'
                          }`}>
                            {insight.category}
                          </span>
                        </div>
                      </div>
                      
                      {/* Confidence Indicator */}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {insight.confidence}%
                        </span>
                        <div className={`w-3 h-3 rounded-full ${
                          insight.confidence >= 90 ? 'bg-green-500' :
                          insight.confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                      {insight.description}
                    </p>
                    
                    {/* Action Recommendation */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-start space-x-2">
                        <span className="text-sm">💡</span>
                        <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                          {insight.action}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Header with Analytics-style Dropdowns */}
      <div className="bg-white dark:bg-slate-900 shadow-sm border-b border-gray-200 dark:border-gray-700 rounded-lg mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Suche nach Name, Straße oder Stadt..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-80 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            
            {/* Analytics-style Dropdowns */}
            <div className="flex items-center space-x-4">
              {/* Owner Dropdown */}
              <div className="relative dropdown-container">
                <button
                  onClick={toggleOwnerDropdown}
                  className="flex items-center justify-between w-48 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-gradient-to-r from-slate-100 via-blue-50 to-slate-200 dark:from-slate-800 dark:via-blue-900/30 dark:to-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:from-slate-200 hover:via-blue-100 hover:to-slate-300 dark:hover:from-slate-700 dark:hover:via-blue-800/40 dark:hover:to-slate-600 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <span className="truncate">{selectedOwnerName}</span>
                  <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${isOwnerDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isOwnerDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOwnerSelect('all', 'Alle Eigentümer');
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none ${
                          selectedOwner === 'all' 
                            ? 'bg-gradient-to-r from-blue-50 via-slate-50 to-blue-50 dark:from-blue-900/20 dark:via-slate-800 dark:to-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Alle Eigentümer
                      </button>
                      {owners.map((owner) => (
                        <button
                          key={owner.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOwnerSelect(owner.id, getOwnerDisplayName(owner));
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none ${
                            selectedOwner === owner.id 
                              ? 'bg-gradient-to-r from-blue-50 via-slate-50 to-blue-50 dark:from-blue-900/20 dark:via-slate-800 dark:to-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' 
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {getOwnerDisplayName(owner)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Property Dropdown */}
              <div className="relative dropdown-container">
                <button
                  onClick={togglePropertyDropdown}
                  className="flex items-center justify-between w-48 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-gradient-to-r from-slate-100 via-green-50 to-slate-200 dark:from-slate-800 dark:via-green-900/30 dark:to-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:from-slate-200 hover:via-green-100 hover:to-slate-300 dark:hover:from-slate-700 dark:hover:via-green-800/40 dark:hover:to-slate-600 transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500/20"
                >
                  <span className="truncate">{selectedPropertyName}</span>
                  <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${isPropertyDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isPropertyDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePropertySelect('all', 'Alle Objekte');
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none ${
                          selectedProperty === 'all' 
                            ? 'bg-gradient-to-r from-green-50 via-slate-50 to-green-50 dark:from-green-900/20 dark:via-slate-800 dark:to-green-900/20 text-green-600 dark:text-green-400 font-medium' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Alle Objekte
                      </button>
                      {properties.map((property) => (
                        <button
                          key={property.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePropertySelect(property.id, getPropertyDisplayName(property));
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none ${
                            selectedProperty === property.id 
                              ? 'bg-gradient-to-r from-green-50 via-slate-50 to-green-50 dark:from-green-900/20 dark:via-slate-800 dark:to-green-900/20 text-green-600 dark:text-green-400 font-medium' 
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {getPropertyDisplayName(property)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-start mt-4">
            <div className="flex items-center space-x-2">
              <button className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                <Filter className="w-4 h-4" />
                Filter
              </button>
              <button className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Grid */}
      {filteredProperties.length === 0 ? (
        <div className="text-center py-12">
          <Building className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Keine Objekte gefunden</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Erstellen Sie Ihr erstes Objekt, um zu beginnen.
          </p>
          <div className="mt-6">
            <button 
              onClick={handleCreateClick}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="-ml-1 mr-2 h-5 w-5" />
              Neues Objekt erstellen
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProperties.map((property) => {
            const metrics = propertyMetrics[property.id] || getDefaultMetrics();
            const performance = getPropertyPerformance(metrics);
            
            return (
              <div
                key={property.id}
                onClick={() => handleEditClick(property)}
                className={`bg-white dark:bg-slate-900 rounded-lg shadow-sm border-2 ${performance.borderClass} hover:shadow-md transition-all duration-300 cursor-pointer`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start min-h-[4rem]">
                      <div className="flex-shrink-0">
                        <Building className="h-8 w-8 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white leading-6">
                          {property.property_name || "Unbenanntes Objekt"}
                        </h3>
                      </div>
                    </div>
                    {/* 🚦 Performance Score Badge */}
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                      performance.color === 'green' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                      performance.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300' :
                      performance.color === 'orange' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300' :
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                    }`}>
                      {performance.score}%
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-start text-sm text-gray-500 dark:text-gray-400 min-h-[2.5rem]">
                      <MapPin className="flex-shrink-0 mr-1.5 h-4 w-4 mt-0.5" />
                      <span className="leading-5">{property.street}, {property.postal_code} {property.city}</span>
                    </div>
                    <div className="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                      <User className="flex-shrink-0 mr-1.5 h-4 w-4" />
                      {owners.find(o => o.id === property.owner_entity)?.name || 'Unbekannt'}
                    </div>
                  </div>

                  {/* 🎨 Cool Progress Bars Section */}
                  <div className="mt-6 space-y-4">
                    {/* Auslastung Progress Bar */}
                    <ProgressBar 
                      percentage={metrics.occupancyRate} 
                      color={metrics.occupancyRate >= 80 ? 'green' : metrics.occupancyRate >= 60 ? 'yellow' : 'red'}
                      label="Auslastung"
                      value={`${metrics.occupancyRate}%`}
                    />

                    {/* ROI Progress Bar */}
                    <ProgressBar 
                      percentage={Math.min(metrics.roi * 12.5, 100)} // 8% ROI = 100%
                      color={metrics.roi >= 6 ? 'green' : metrics.roi >= 4 ? 'yellow' : 'red'}
                      label="ROI"
                      value={`${metrics.roi}%`}
                    />
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {property.units || 0} Einheiten
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {metrics.occupancyRate}% Belegt
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        📄 {metrics.documentCount || 0} Dokumente
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Monatliche Einnahmen</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          €{metrics.monthlyRent.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500 dark:text-gray-400">Cashflow</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          €{metrics.cashflow.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnitsClick(property);
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-md flex items-center justify-center" title="Einheitenübersicht und Bearbeitung"
                    >
                      <Home className="w-4 h-4 mr-2" />
                      Einheiten
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(property);
                      }}
                      className="bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium py-2 px-4 rounded-md" title="Objekt bearbeiten"
                    >
                      Bearbeiten
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        generateClaraInsights(property);
                      }}
                      disabled={generatingInsights}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white p-2 rounded-md transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50"
                      title="Clara Insights"
                    >
                      {generatingInsights ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                    </button>
                    <button 
                      onClick={(e) => e.stopPropagation()}
                      className="bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-300 p-2 rounded-md" title="Objekt löschen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ULTIMATE PROPERTY MODAL WITH LIVE INSIGHTS */}
      {(showEditModal || showCreateModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col">
            
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Building className="w-8 h-8" />
                  <div>
                    <h2 className="text-2xl font-bold">
                      {selectedPropertyForEdit ? 'Immobilie bearbeiten' : 'Neue Immobilie'}
                    </h2>
                    <p className="text-blue-100 text-sm">
                      Verwalten Sie alle Immobiliendaten und erhalten Sie intelligente Analysen
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setShowCreateModal(false);
                    setSelectedPropertyForEdit(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Main Content */}
              <div className="flex-1 flex flex-col p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Grunddaten */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
                      <Home className="w-5 h-5 mr-2" />
                      Grunddaten
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Objektname *
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.property_name || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. Mehrfamilienhaus Musterstraße"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Straße
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.street || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. Musterstraße 123"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Stadt
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.city || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. Mannheim"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          PLZ
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.postal_code || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 68169"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Immobilientyp
                        </label>
                        <select
                          defaultValue={selectedPropertyForEdit?.property_type || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Bitte wählen</option>
                          <option value="Mehrfamilienhaus">Mehrfamilienhaus</option>
                          <option value="Einfamilienhaus">Einfamilienhaus</option>
                          <option value="Doppelhaushälfte">Doppelhaushälfte</option>
                          <option value="Reihenhaus">Reihenhaus</option>
                          <option value="Eigentumswohnung">Eigentumswohnung</option>
                          <option value="Gewerbeimmobilie">Gewerbeimmobilie</option>
                          <option value="Bürogebäude">Bürogebäude</option>
                          <option value="Einzelhandel">Einzelhandel</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Anzahl Einheiten
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.units || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 8"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Land
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.country || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. Deutschland"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Vollständige Adresse
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.address || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. Musterstraße 123, 68169 Mannheim"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Immobilienstatus
                        </label>
                        <select
                          defaultValue={selectedPropertyForEdit?.property_status || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Bitte wählen</option>
                          <option value="Aktiv">Aktiv</option>
                          <option value="Verkauft">Verkauft</option>
                          <option value="Reserviert">Reserviert</option>
                          <option value="Inaktiv">Inaktiv</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Finanzielle Daten */}
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-4 flex items-center">
                      <DollarSign className="w-5 h-5 mr-2" />
                      Finanzielle Daten
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kaufpreis (€)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.purchase_price || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 450000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kaufdatum
                        </label>
                        <input
                          type="date"
                          defaultValue={selectedPropertyForEdit?.purchase_date || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Monatliche Mieteinnahmen (€)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.monthly_rent_total || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 3200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Monatliche Kosten (€)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.monthly_costs || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 1800"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Geschätzter Marktwert (€)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.estimated_market_value || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 520000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Eigenkapital (€)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.financing_equity || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 90000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Fremdkapital (€)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.financing_debt || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 360000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Bodenanteil (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          defaultValue={selectedPropertyForEdit?.landshare_percent || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 25.5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          AfA-Satz Überschreibung (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          defaultValue={selectedPropertyForEdit?.depreciation_rate_override || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 2.0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Technische Daten */}
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                    <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center">
                      <Building className="w-5 h-5 mr-2" />
                      Technische Daten
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Baujahr
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.year_built || selectedPropertyForEdit?.construction_year || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 1985"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Gesamtfläche (m²)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.total_square_meters || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 450"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Vermietbare Fläche (m²)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.rentable_area || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 380"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Energieeffizienzklasse
                        </label>
                        <select
                          defaultValue={selectedPropertyForEdit?.energy_efficiency_class || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Bitte wählen</option>
                          <option value="A+">A+</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                          <option value="E">E</option>
                          <option value="F">F</option>
                          <option value="G">G</option>
                          <option value="H">H</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Grundstücksfläche (m²)
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.ground_area || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 800"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Anzahl Etagen
                        </label>
                        <input
                          type="number"
                          defaultValue={selectedPropertyForEdit?.number_of_floors || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. 3"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Verwaltung */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                    <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-4 flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      Verwaltung
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Eigentümer
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.owner_entity || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. Max Mustermann GmbH"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Bank
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.bank_name || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. Sparkasse Rhein Neckar Nord"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Bankkonto
                        </label>
                        <input
                          type="text"
                          defaultValue={selectedPropertyForEdit?.bank_account || ""}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                          placeholder="z.B. DE89 3705 0198 1234 5678 90"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notizen */}
                  <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                    <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2" />
                      Notizen
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Allgemeine Notizen
                      </label>
                      <textarea
                        defaultValue={selectedPropertyForEdit?.notes || ""}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        placeholder="Zusätzliche Informationen zur Immobilie..."
                      />
                    </div>
                  </div>

                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                        setShowCreateModal(false);
                        setSelectedPropertyForEdit(null);
                      }}
                      className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={() => {
                        handlePropertySave(); return;
                        setShowEditModal(false);
                        setShowCreateModal(false);
                        setSelectedPropertyForEdit(null);
                      }}
                      className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>{showCreateModal ? "Objekt erstellen" : "Änderungen speichern"}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Live Insights Sidebar */}
              <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-black overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-6">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Insights</h3>
                  </div>

                  {/* Finanzielle KPIs */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                      <Calculator className="w-4 h-4 mr-2" />
                      Finanzielle Kennzahlen
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Bruttorendite</span>
                          {(() => {
                          const metrics = propertyMetrics[selectedPropertyForEdit?.id] || getDefaultMetrics();
                          const roi = metrics.roi || 0;
                          return <span className="text-sm font-semibold text-green-600">{roi.toFixed(1)}%</span>;
                        })()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Nettorendite</span>
                          {(() => {
                          const metrics = propertyMetrics[selectedPropertyForEdit?.id] || getDefaultMetrics();
                          const roi = metrics.roi || 0;
                          const nettorendite = roi * 0.6; // 60% after costs
                          return <span className="text-sm font-semibold text-green-600">{nettorendite.toFixed(1)}%</span>;
                        })()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Cashflow/Jahr</span>
                          {(() => {
                          const metrics = propertyMetrics[selectedPropertyForEdit?.id] || getDefaultMetrics();
                          const monthlyCashflow = metrics.cashflow || 0;
                          const yearlyCashflow = monthlyCashflow * 12;
                          return <span className="text-sm font-semibold text-green-600">{formatCurrency(yearlyCashflow)}</span>;
                        })()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Miete/m²</span>
                          {(() => {
                          const metrics = propertyMetrics[selectedPropertyForEdit?.id] || getDefaultMetrics();
                          const monthlyRent = metrics.monthlyRent || 0;
                          const totalSqm = selectedPropertyForEdit?.total_square_meters || selectedPropertyForEdit?.rentable_area || 1;
                          const rentPerSqm = totalSqm > 0 ? (monthlyRent / totalSqm).toFixed(2) : 0;
                          return <span className="text-sm font-semibold text-blue-600">€{rentPerSqm}/m²</span>;
                        })()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Eigenkapitalquote</span>
                          {(() => {
                          const equity = selectedPropertyForEdit?.financing_equity || 0;
                          const debt = selectedPropertyForEdit?.financing_debt || 0;
                          const total = equity + debt;
                          const equityRatio = total > 0 ? ((equity / total) * 100).toFixed(1) : 0;
                          return <span className="text-sm font-semibold text-blue-600">{equityRatio}%</span>;
                        })()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">AfA/Jahr</span>
                          {(() => {
                          const purchasePrice = selectedPropertyForEdit?.purchase_price || 0;
                          const landshare = selectedPropertyForEdit?.landshare_percent || 20;
                          const buildingValue = purchasePrice * (1 - landshare / 100);
                          const afaRate = selectedPropertyForEdit?.depreciation_rate_override || 2;
                          const yearlyAfa = buildingValue * (afaRate / 100);
                          return <span className="text-sm font-semibold text-orange-600">{formatCurrency(yearlyAfa)}</span>;
                        })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Finanzierungs-Analyse */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Finanzierungs-Struktur
                    </h4>
                    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Eigenkapital</span>
                        {(() => {
                          const equity = selectedPropertyForEdit?.financing_equity || 0;
                          return <span className="text-sm font-semibold text-green-600">{formatCurrency(equity)}</span>;
                        })()}
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-slate-800 rounded-full h-2 mb-2">
                        {(() => {
                          const equity = selectedPropertyForEdit?.financing_equity || 0;
                          const debt = selectedPropertyForEdit?.financing_debt || 0;
                          const total = equity + debt;
                          const equityRatio = total > 0 ? ((equity / total) * 100).toFixed(1) : 0;
                          return <div className="bg-green-500 h-2 rounded-full" style={{width: `${equityRatio}%`}}></div>;
                        })()}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Fremdkapital</span>
                        {(() => {
                          const debt = selectedPropertyForEdit?.financing_debt || 0;
                          return <span className="text-sm font-semibold text-red-600">{formatCurrency(debt)}</span>;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Wertentwicklung */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                      <Calculator className="w-4 h-4 mr-2" />
                      Wertentwicklung
                    </h4>
                    <div className="space-y-2">
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Kaufpreis (2018)</span>
                          {(() => {
                          const purchasePrice = selectedPropertyForEdit?.purchase_price || 0;
                          return <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(purchasePrice)}</span>;
                        })()}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Aktueller Wert</span>
                          {(() => {
                          const currentValue = selectedPropertyForEdit?.estimated_market_value || 0;
                          return <span className="text-sm font-semibold text-green-600">{formatCurrency(currentValue)}</span>;
                        })()}
                        </div>
                      </div>
                      {(() => {
                          const purchasePrice = selectedPropertyForEdit?.purchase_price || 0;
                          const currentValue = selectedPropertyForEdit?.estimated_market_value || 0;
                          const increase = currentValue - purchasePrice;
                          const increasePercent = purchasePrice > 0 ? ((increase / purchasePrice) * 100).toFixed(1) : 0;
                          const sign = increase >= 0 ? '+' : '';
                          const bgClass = increase >= 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
                          const textClass = increase >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300';
                          const textBoldClass = increase >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300';
                          const textSmallClass = increase >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                          
                          return (
                            <div className={`p-3 rounded-lg border ${bgClass}`}>
                              <div className="flex justify-between items-start gap-4">
                                <span className={`text-sm ${textClass} flex-shrink-0`}>Wertsteigerung</span>
                                <div className="text-right">
                                  <div className={`text-sm font-bold ${textBoldClass}`}>
                                    {sign}{formatCurrency(increase)}
                                  </div>
                                  <div className={`text-xs ${textSmallClass} mt-0.5`}>
                                    ({sign}{increasePercent}%)
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  </div>

                  {/* Empfehlungen */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Empfehlungen
                    </h4>
                    <div className="space-y-3">
                      {/* Dynamische Empfehlungen basierend auf echten Werten */}
                      {(() => {
                        const metrics = propertyMetrics[selectedPropertyForEdit?.id] || getDefaultMetrics();
                        const roi = metrics.roi || 0;
                        const purchasePrice = selectedPropertyForEdit?.purchase_price || 0;
                        const currentValue = selectedPropertyForEdit?.estimated_market_value || 0;
                        const increase = currentValue - purchasePrice;
                        const increasePercent = purchasePrice > 0 ? ((increase / purchasePrice) * 100).toFixed(1) : 0;
                        const equity = selectedPropertyForEdit?.financing_equity || 0;
                        const debt = selectedPropertyForEdit?.financing_debt || 0;
                        const total = equity + debt;
                        const equityRatio = total > 0 ? ((equity / total) * 100).toFixed(1) : 0;
                        const landshare = selectedPropertyForEdit?.landshare_percent || 20;
                        const buildingValue = purchasePrice * (1 - landshare / 100);
                        const afaRate = selectedPropertyForEdit?.depreciation_rate_override || 2;
                        const yearlyAfa = buildingValue * (afaRate / 100);
                        
                        const recommendations = [];
                        
                        // Solide Rendite (nur wenn ROI > 4%)
                        if (roi > 4) {
                          recommendations.push(
                            <div key="roi" className="p-3 rounded-lg border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                              <div className="flex items-start space-x-2">
                                <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-green-900 dark:text-green-100">
                                    Solide Rendite
                                  </h5>
                                  <p className="text-xs mt-1 text-green-700 dark:text-green-300">
                                    Die Bruttorendite von {roi.toFixed(1)}% liegt über dem Marktdurchschnitt
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Refinanzierung prüfen (nur wenn Wertsteigerung > 10%)
                        if (increasePercent > 10) {
                          recommendations.push(
                            <div key="refi" className="p-3 rounded-lg border bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
                              <div className="flex items-start space-x-2">
                                <AlertCircle className="w-4 h-4 mt-0.5 text-orange-600" />
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-orange-900 dark:text-orange-100">
                                    Refinanzierung prüfen
                                  </h5>
                                  <p className="text-xs mt-1 text-orange-700 dark:text-orange-300">
                                    Bei {increasePercent}% Wertsteigerung könnte eine Refinanzierung sinnvoll sein
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // AfA optimieren (nur wenn AfA > 0)
                        if (yearlyAfa > 0) {
                          recommendations.push(
                            <div key="afa" className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                              <div className="flex items-start space-x-2">
                                <TrendingUp className="w-4 h-4 mt-0.5 text-blue-600" />
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                    AfA optimieren
                                  </h5>
                                  <p className="text-xs mt-1 text-blue-700 dark:text-blue-300">
                                    Mit {afaRate}% AfA-Satz sparen Sie {formatCurrency(yearlyAfa)}/Jahr an Steuern
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Eigenkapital-Effizienz (nur wenn Quote zwischen 15-30%)
                        if (equityRatio >= 15 && equityRatio <= 30) {
                          recommendations.push(
                            <div key="equity" className="p-3 rounded-lg border bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
                              <div className="flex items-start space-x-2">
                                <Calculator className="w-4 h-4 mt-0.5 text-purple-600" />
                                <div className="flex-1">
                                  <h5 className="text-sm font-medium text-purple-900 dark:text-purple-100">
                                    Eigenkapital-Effizienz
                                  </h5>
                                  <p className="text-xs mt-1 text-purple-700 dark:text-purple-300">
                                    {equityRatio}% Eigenkapitalquote ist optimal für Immobilieninvestments
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Modernisierungspotential (immer anzeigen)
                        recommendations.push(
                          <div key="modern" className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                            <div className="flex items-start space-x-2">
                              <AlertCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                              <div className="flex-1">
                                <h5 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                  Modernisierungspotential
                                </h5>
                                <p className="text-xs mt-1 text-blue-700 dark:text-blue-300">
                                  Energetische Sanierung könnte Miete und Wert steigern
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                        
                        return recommendations;
                      })()}

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Units Modal */}
      {showUnitsModal && selectedPropertyForUnits && (
        <UnitsManagerModal
          isOpen={showUnitsModal}
          property={selectedPropertyForUnits}
          onClose={() => {
            setShowUnitsModal(false);
            setSelectedPropertyForUnits(null);
          }}
          onUpdate={loadProperties}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default PropertiesPage;


  // 🧠 ANALYSIS FUNCTIONS

  // Analyze Contracts and Lease Data
  const analyzeContracts = (tenants, units) => {
    const now = new Date();
    const sixMonthsFromNow = new Date(now.getTime() + 6 * 30 * 24 * 60 * 60 * 1000);
    const twentyFourMonthsFromNow = new Date(now.getTime() + 24 * 30 * 24 * 60 * 60 * 1000); // CHANGED: 12 → 24 months
    
    // Find expiring contracts
    const expiringIn6Months = tenants.filter(t => 
      t.lease_end && new Date(t.lease_end) <= sixMonthsFromNow && new Date(t.lease_end) > now
    );
    
    const expiringIn24Months = tenants.filter(t => 
      t.lease_end && new Date(t.lease_end) <= twentyFourMonthsFromNow && new Date(t.lease_end) > now
    );

    // FIXED: Calculate actual lease duration (how long tenants have been there)
    const activeTenants = tenants.filter(t => t.lease_start);
    const avgLeaseDuration = activeTenants.length > 0 
      ? activeTenants.reduce((sum, t) => {
          const start = new Date(t.lease_start);
          const today = new Date(); // FIXED: Use today instead of lease_end
          return sum + (today - start) / (1000 * 60 * 60 * 24 * 30); // months
        }, 0) / activeTenants.length
      : 0;

    return [
      {
        icon: "⏰",
        title: "Auslaufende Verträge",
        description: `${expiringIn6Months.length} Verträge laufen in 6 Monaten aus${expiringIn6Months.length > 0 ? ` - Verlängerung jetzt verhandeln` : ''}`,
        category: "Verträge",
        type: expiringIn6Months.length > 0 ? 'warning' : 'success',
        confidence: 95,
        action: expiringIn6Months.length > 0 ? "Verlängerungsgespräche einleiten" : "Vertragsmanagement läuft optimal"
      },
      {
        icon: "📊",
        title: "Vertragsstabilität", 
        description: `Durchschnittliche Mietdauer: ${avgLeaseDuration.toFixed(1)} Monate${avgLeaseDuration > 36 ? ' - Sehr stabile Mieterschaft' : avgLeaseDuration > 24 ? ' - Gute Mieterbindung' : ' - Hohe Fluktuation'}`,
        category: "Verträge",
        type: avgLeaseDuration > 36 ? 'success' : avgLeaseDuration > 24 ? 'warning' : 'error',
        confidence: 95, // INCREASED: More confident with correct calculation
        action: avgLeaseDuration < 24 ? "Mieterzufriedenheit analysieren" : "Mieterbindung weiter stärken"
      },
      {
        icon: "🔄",
        title: "Verlängerungspotenzial",
        description: `${expiringIn24Months.length} Verträge zur Verlängerung in 24 Monaten - Planungszeit nutzen`, // CHANGED: 12 → 24 months
        category: "Verträge", 
        type: 'info',
        confidence: 90, // INCREASED: 24 months is more realistic timeframe
        action: "Verlängerungsstrategie entwickeln"
      }
    ];
  };

  // Analyze Economic Performance and Optimization
  const analyzeEconomics = (tenants, units, property, metrics) => {
    // Calculate rent per sqm
    const rentPerSqm = tenants.length > 0 && units.length > 0
      ? tenants.reduce((sum, t) => sum + (parseFloat(t.cold_rent) || 0), 0) / 
        units.reduce((sum, u) => sum + (parseFloat(u.size_sqm) || 0), 0)
      : 0;

    // Market comparison - UPDATED with current Mannheim market data (2025)
    const marketRentPerSqm = 11.07; // €/sqm - ImmoScout24 Mannheim average 2025 (was 12)
    const rentGap = ((marketRentPerSqm - rentPerSqm) / marketRentPerSqm) * 100;
    
    // ROI optimization potential
    const currentROI = metrics.roi;
    const optimizedRent = metrics.monthlyRent * (1 + Math.max(0, rentGap / 100));
    const optimizedROI = property.purchase_price > 0 
      ? ((optimizedRent * 12) / property.purchase_price) * 100 
      : currentROI;
    
    const roiIncrease = optimizedROI - currentROI;

    return [
      {
        icon: "💶",
        title: "Mietpreis-Optimierung",
        description: `Aktuelle Miete ${rentGap > 5 ? `${rentGap.toFixed(1)}% unter Markt` : rentGap < -5 ? `${Math.abs(rentGap).toFixed(1)}% über Markt` : 'marktgerecht'} - Potenzial: €${(optimizedRent - metrics.monthlyRent).toFixed(0)}/Monat`,
        category: "Wirtschaft",
        type: rentGap > 10 ? 'warning' : rentGap > 5 ? 'info' : 'success',
        confidence: 75,
        action: rentGap > 5 ? "Mietanpassung bei Neuvermietung prüfen" : "Mietpreis optimal"
      },
      {
        icon: "📈",
        title: "ROI-Steigerungspotenzial",
        description: `ROI könnte von ${currentROI.toFixed(1)}% auf ${optimizedROI.toFixed(1)}% steigen (+${roiIncrease.toFixed(1)} Prozentpunkte)`,
        category: "Wirtschaft",
        type: roiIncrease > 2 ? 'warning' : roiIncrease > 1 ? 'info' : 'success',
        confidence: 70,
        action: roiIncrease > 1 ? "Mietoptimierung bei Neuvermietung" : "ROI bereits optimiert"
      },
      {
        icon: "⚖️",
        title: "Kosten-Nutzen-Analyse",
        description: `Cashflow-Marge: ${((metrics.cashflow / metrics.monthlyRent) * 100).toFixed(0)}%${metrics.cashflow / metrics.monthlyRent > 0.3 ? ' - Sehr effizient' : metrics.cashflow / metrics.monthlyRent > 0.2 ? ' - Gut' : ' - Optimierungsbedarf'}`,
        category: "Wirtschaft",
        type: metrics.cashflow / metrics.monthlyRent > 0.3 ? 'success' : metrics.cashflow / metrics.monthlyRent > 0.2 ? 'warning' : 'error',
        confidence: 85,
        action: metrics.cashflow / metrics.monthlyRent < 0.2 ? "Kostenstruktur überprüfen" : "Effizienz beibehalten"
      }
    ];
  };

  // Analyze Maintenance Patterns and Predictions
  const analyzeMaintenance = (maintenanceData, property, units) => {
    const totalMaintenanceCost = maintenanceData.reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0);
    const avgCostPerUnit = units.length > 0 ? totalMaintenanceCost / units.length : 0;
    const maintenanceFrequency = maintenanceData.length;
    
    // Calculate cost per sqm
    const totalSqm = units.reduce((sum, u) => sum + (parseFloat(u.size_sqm) || 0), 0);
    const costPerSqm = totalSqm > 0 ? totalMaintenanceCost / totalSqm : 0;
    
    // Property age analysis
    const currentYear = new Date().getFullYear();
    const propertyAge = property.year_built ? currentYear - property.year_built : 0;
    
    return [
      {
        icon: "💸",
        title: "Wartungskosten-Analyse",
        description: `€${totalMaintenanceCost.toFixed(0)} Wartungskosten (€${costPerSqm.toFixed(2)}/m²)${costPerSqm > 15 ? ' - Überdurchschnittlich' : costPerSqm > 10 ? ' - Normal' : ' - Niedrig'}`,
        category: "Wartung",
        type: costPerSqm > 15 ? 'warning' : costPerSqm > 10 ? 'info' : 'success',
        confidence: 80,
        action: costPerSqm > 15 ? "Kostentreiber identifizieren" : "Wartungskosten im Rahmen"
      },
      {
        icon: "🔍",
        title: "Wartungsfrequenz",
        description: `${maintenanceFrequency} Wartungsfälle${maintenanceFrequency > 20 ? ' - Häufige Reparaturen' : maintenanceFrequency > 10 ? ' - Normale Wartung' : ' - Wenig Wartungsbedarf'}`,
        category: "Wartung",
        type: maintenanceFrequency > 20 ? 'warning' : maintenanceFrequency > 10 ? 'info' : 'success',
        confidence: 75,
        action: maintenanceFrequency > 20 ? "Präventive Maßnahmen prüfen" : "Wartungsrhythmus optimal"
      },
      {
        icon: "🏗️",
        title: "Alters-Kosten-Korrelation",
        description: `Objektalter: ${propertyAge} Jahre${propertyAge > 30 ? ' - Erhöhter Wartungsbedarf erwartet' : propertyAge > 15 ? ' - Moderate Wartungskosten' : ' - Niedrige Wartungskosten'}`,
        category: "Wartung",
        type: propertyAge > 30 ? 'warning' : propertyAge > 15 ? 'info' : 'success',
        confidence: 70,
        action: propertyAge > 30 ? "Modernisierungsplan entwickeln" : "Wartungsplanung ausreichend"
      }
    ];
  };

  // Analyze Market Trends and Predictions
  const analyzeMarketTrends = (property, tenants, metrics) => {
    // Vacancy risk analysis
    const vacancyRisk = metrics.occupancyRate < 80 ? 'high' : metrics.occupancyRate < 90 ? 'medium' : 'low';
    
    // Investment timing analysis
    const investmentScore = (metrics.roi > 6 ? 30 : metrics.roi > 4 ? 20 : 10) + 
                           (metrics.occupancyRate > 90 ? 30 : metrics.occupancyRate > 80 ? 20 : 10) +
                           (metrics.cashflow > 0 ? 40 : 0);
    
    return [
      {
        icon: "🎯",
        title: "Leerstand-Risiko",
        description: `Leerstand-Risiko: ${vacancyRisk === 'low' ? 'Niedrig' : vacancyRisk === 'medium' ? 'Mittel' : 'Hoch'} - Aktuelle Auslastung ${metrics.occupancyRate}%`,
        category: "Markt",
        type: vacancyRisk === 'low' ? 'success' : vacancyRisk === 'medium' ? 'warning' : 'error',
        confidence: 75,
        action: vacancyRisk === 'high' ? "Marketing-Strategie überarbeiten" : "Auslastung stabil"
      },
      {
        icon: "📊",
        title: "Investitions-Timing",
        description: `Investment-Score: ${investmentScore}/100${investmentScore > 80 ? ' - Exzellente Investition' : investmentScore > 60 ? ' - Gute Investition' : ' - Verbesserungspotenzial'}`,
        category: "Markt",
        type: investmentScore > 80 ? 'success' : investmentScore > 60 ? 'info' : 'warning',
        confidence: 65,
        action: investmentScore < 60 ? "Optimierungsmaßnahmen prüfen" : "Investment-Performance gut"
      },
      {
        icon: "🔮",
        title: "Zukunftsprognose",
        description: `Basierend auf Trends: ${metrics.roi > 6 && metrics.occupancyRate > 85 ? 'Positive Entwicklung erwartet' : 'Stabile Entwicklung erwartet'}`,
        category: "Markt",
        type: 'info',
        confidence: 60,
        action: "Regelmäßige Marktbeobachtung fortsetzen"
      }
    ];
  };

